<template>
  <div class="page-view">

    <BasePageHeader :title="$t('locations.title')" @back="$router.back()">
      <template #actions>
        <router-link v-if="canManageLocations" to="/bulk-edit/locations" class="btn ghost small">{{ $t('bulkEdit.title') }}</router-link>
        <router-link v-if="canManageLocations" to="/bulk-edit/warehouses" class="btn ghost small">{{ $t('bulkEdit.warehouses') }}</router-link>
        <button v-if="canManageLocations" class="btn primary sm" @click="openCreate">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
          {{ $t('common.new') }}
        </button>
      </template>
    </BasePageHeader>

    <div class="px-4 pt-3.5 pb-2">
      <SearchField v-model="query" :debounce="0" :placeholder="$t('locations.searchPlaceholder')" />
    </div>

    <!-- Location create form (new only) -->
    <div v-if="formOpen && !editing" class="mx-4 my-2 p-4 rounded-xl flex flex-col gap-2.5 border border-[var(--color-border)]" style="background:var(--color-surface)">
      <div class="text-sm font-semibold">{{ $t('locations.newLocation') }}</div>
      <BaseFormField :label="$t('common.name')" required>
        <BaseInput v-model="form.name" :placeholder="$t('locations.namePlaceholder')" />
      </BaseFormField>
      <BaseFormField :label="$t('locations.addressPlaceholder')" required>
        <BaseInput v-model="form.address" :placeholder="$t('locations.addressPlaceholder')" />
      </BaseFormField>
      <BaseFormField :label="$t('common.description')">
        <BaseInput v-model="form.description" :placeholder="$t('common.description')" />
      </BaseFormField>
      <BaseFormActions
        :confirm-label="$t('common.create')"
        :loading="saving"
        :disabled="!form.name || !form.address"
        @cancel="closeForm"
        @confirm="save"
      />
    </div>

    <div v-if="loading" class="state-empty">{{ $t('common.loading') }}</div>
    <div v-else-if="loadError" class="state-empty error">{{ loadError }}</div>
    <div v-else-if="!filtered.length" class="state-empty">
      {{ query ? $t('locations.noMatch') : $t('locations.noLocations') }}
    </div>

    <div v-else class="list-stack">
      <template v-for="loc in filtered" :key="loc.id">

        <!-- Inline location edit form -->
        <div v-if="editing?.id === loc.id" ref="inlineLocFormEl" class="p-4 rounded-xl flex flex-col gap-2.5 border border-[var(--color-border)]" style="background:var(--color-surface)">
          <div class="text-sm font-semibold">{{ $t('locations.editLocation') }}</div>
          <BaseFormField :label="$t('common.name')" required>
            <BaseInput v-model="form.name" :placeholder="$t('locations.namePlaceholder')" />
          </BaseFormField>
          <BaseFormField :label="$t('locations.addressPlaceholder')" required>
            <BaseInput v-model="form.address" :placeholder="$t('locations.addressPlaceholder')" />
          </BaseFormField>
          <BaseFormField :label="$t('common.description')">
            <BaseInput v-model="form.description" :placeholder="$t('common.description')" />
          </BaseFormField>
          <BaseFormActions
            :confirm-label="$t('common.update')"
            :loading="saving"
            :disabled="!form.name || !form.address"
            @cancel="closeForm"
            @confirm="save"
          />
        </div>

        <!-- Normal location row + children -->
        <div v-else class="flex flex-col">

        <!-- Location row -->
        <div class="data-card" :class="warehousesFor(loc.id).length || warehouseFormLocationId === loc.id ? 'rounded-b-none border-b-transparent' : ''">
          <div class="data-card__main">
            <div class="data-card__name">{{ loc.name }}</div>
            <div class="data-card__meta">
              <span v-if="loc.address" class="badge badge-accent">{{ loc.address }}</span>
              <span v-if="warehousesFor(loc.id).length" class="text-xs text-[var(--color-muted)] mt-1">{{ warehousesFor(loc.id).length }} {{ warehousesFor(loc.id).length === 1 ? $t('entity.warehouse') : $t('entity.warehouse_plural') }}</span>
              <span v-if="loc.description" class="text-xs text-[var(--color-muted)] mt-1">{{ loc.description }}</span>
            </div>
          </div>
          <div class="data-card__actions">
            <BaseIconButton v-if="canManageLocations" class="!bg-blue-900/60 !text-blue-300 hover:!bg-blue-800/60" :title="$t('locations.addWarehouse')" @click="openWarehouseCreate(loc.id)">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
            </BaseIconButton>
            <BaseIconButton v-if="canManageLocations" :title="$t('common.edit')" @click="openEdit(loc)">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4Z"/></svg>
            </BaseIconButton>
            <BaseIconButton v-if="canManageLocations" variant="danger" :title="$t('common.delete')" @click="remove(loc)">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/><path d="M10 11v6"/><path d="M14 11v6"/></svg>
            </BaseIconButton>
          </div>
        </div>

        <!-- Warehouse children -->
        <div v-if="warehousesFor(loc.id).length || warehouseFormLocationId === loc.id" class="flex flex-col border border-t-0 border-[var(--color-border)] rounded-b-[.75rem] overflow-hidden">
          <div v-for="wh in warehousesFor(loc.id)" :key="wh.id" class="border-t border-[var(--color-border)] ml-5 rounded-none">
            <div v-if="warehouseEditing?.id === wh.id" class="flex flex-col gap-2 px-4 py-2.5" style="background: var(--color-surface-2)">
              <div class="text-xs font-semibold text-[var(--color-muted)]">{{ $t('locations.editWarehouse') }}</div>
              <BaseFormField :label="$t('common.name')" required>
                <BaseInput v-model="warehouseForm.name" :placeholder="$t('locations.warehouseNamePlaceholder')" @keydown.enter="saveWarehouse" @keydown.escape="closeWarehouseForm" />
              </BaseFormField>
              <BaseFormActions
                :confirm-label="$t('common.update')"
                :loading="warehouseSaving"
                :disabled="!warehouseForm.name"
                @cancel="closeWarehouseForm"
                @confirm="saveWarehouse"
              />
            </div>
            <div v-else class="data-card border-none rounded-none" style="background: var(--color-surface-2)">
            <div class="data-card__main">
              <div class="data-card__name">{{ wh.name }}</div>
            </div>
            <div class="data-card__actions">
              <BaseIconButton v-if="canManageLocations" :title="$t('common.edit')" @click="openWarehouseEdit(wh)">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4Z"/></svg>
              </BaseIconButton>
              <BaseIconButton v-if="canManageLocations" variant="danger" :title="$t('common.delete')" @click="removeWarehouse(wh)">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/><path d="M10 11v6"/><path d="M14 11v6"/></svg>
              </BaseIconButton>
            </div>
            </div>
          </div>

          <!-- New warehouse form -->
          <div v-if="warehouseFormLocationId === loc.id && !warehouseEditing" class="flex flex-col gap-2 px-4 py-2.5 border-t border-[var(--color-border)]" style="background: var(--color-surface-2)">
            <BaseFormField :label="$t('common.name')" required>
              <BaseInput v-model="warehouseForm.name" :placeholder="$t('locations.warehouseNamePlaceholder')" @keydown.enter="saveWarehouse" @keydown.escape="closeWarehouseForm" />
            </BaseFormField>
            <BaseFormActions
              :confirm-label="$t('common.add')"
              :loading="warehouseSaving"
              :disabled="!warehouseForm.name"
              @cancel="closeWarehouseForm"
              @confirm="saveWarehouse"
            />
          </div>
        </div>

        </div>
      </template>
    </div>

  </div>
</template>

<script setup>
import { ref, computed, onMounted, nextTick } from 'vue'
import BaseFormField from '@/components/base/BaseFormField.vue'
import BaseInput from '@/components/base/BaseInput.vue'
import BaseFormActions from '@/components/base/BaseFormActions.vue'
import { useToast } from '@/composables/useToast'
import { usePermissions } from '@/composables/usePermissions.js'
import BasePageHeader from '@/components/base/BasePageHeader.vue'
import BaseIconButton from '@/components/base/BaseIconButton.vue'
import { getLocations, createLocation, updateLocation, deleteLocation, getWarehouses, postWarehouse, updateWarehouse, deleteWarehouse } from '@/api/api.js'
import SearchField from '@/components/search/SearchField.vue'
import { useI18n } from 'vue-i18n'

const toast = useToast()
const { canManageLocations } = usePermissions()
const { t } = useI18n()
const query = ref('')
const locations = ref([])
const warehouses = ref([])
const loading = ref(false)
const loadError = ref(null)
const formOpen = ref(false)
const editing = ref(null)
const saving = ref(false)
const form = ref({ name: '', address: '', description: '' })
const inlineLocFormEl = ref(null)

// warehouse inline form state
const warehouseFormLocationId = ref(null)  // which location's form is open
const warehouseEditing = ref(null)
const warehouseSaving = ref(false)
const warehouseForm = ref({ name: '' })

const filtered = computed(() => {
  const q = query.value.trim().toLowerCase()
  if (!q) return locations.value
  return locations.value.filter(l =>
    l.name?.toLowerCase().includes(q) ||
    l.address?.toLowerCase().includes(q) ||
    l.description?.toLowerCase().includes(q)
  )
})

function warehousesFor(locationId) {
  return warehouses.value.filter(w => w.location === locationId)
}

async function load() {
  loading.value = true
  loadError.value = null
  try {
    const [locRes, whRes] = await Promise.all([getLocations(), getWarehouses()])
    locations.value = locRes.results ?? locRes ?? []
    warehouses.value = whRes.results ?? whRes ?? []
  } catch {
    loadError.value = t('locations.loadFailed')
  } finally {
    loading.value = false
  }
}

function openCreate() {
  editing.value = null
  form.value = { name: '', address: '', description: '' }
  formOpen.value = true
}

function openEdit(loc) {
  editing.value = loc
  form.value = { name: loc.name, address: loc.address, description: loc.description ?? '' }
  formOpen.value = true
  nextTick(() => {
    const el = Array.isArray(inlineLocFormEl.value) ? inlineLocFormEl.value[0] : inlineLocFormEl.value
    el?.scrollIntoView({ behavior: 'smooth', block: 'center' })
  })
}

function closeForm() {
  formOpen.value = false
  editing.value = null
}

async function save() {
  saving.value = true
  try {
    if (editing.value) {
      const updated = await updateLocation(editing.value.id, form.value)
      const idx = locations.value.findIndex(l => l.id === editing.value.id)
      if (idx !== -1) locations.value[idx] = updated
      toast.success(t('locations.updated'))
    } else {
      const created = await createLocation(form.value)
      locations.value.unshift(created)
      toast.success(t('locations.created'))
    }
    closeForm()
  } catch {
    toast.error(t('locations.saveFailed'))
  } finally {
    saving.value = false
  }
}

async function remove(loc) {
  if (!confirm(t('locations.deleteConfirm', { name: loc.name }))) return
  try {
    await deleteLocation(loc.id)
    locations.value = locations.value.filter(l => l.id !== loc.id)
    warehouses.value = warehouses.value.filter(w => w.location !== loc.id)
    toast.success(t('locations.deleted'))
  } catch {
    toast.error(t('locations.deleteFailed'))
  }
}

// ─── warehouse actions ────────────────────────────────────────────────────────
function openWarehouseCreate(locationId) {
  warehouseEditing.value = null
  warehouseForm.value = { name: '' }
  warehouseFormLocationId.value = locationId
}

function openWarehouseEdit(wh) {
  warehouseEditing.value = wh
  warehouseForm.value = { name: wh.name }
  warehouseFormLocationId.value = wh.location
}

function closeWarehouseForm() {
  warehouseFormLocationId.value = null
  warehouseEditing.value = null
}

async function saveWarehouse() {
  if (!warehouseForm.value.name) return
  warehouseSaving.value = true
  try {
    if (warehouseEditing.value) {
      const updated = await updateWarehouse(warehouseEditing.value.id, { name: warehouseForm.value.name })
      const idx = warehouses.value.findIndex(w => w.id === warehouseEditing.value.id)
      if (idx !== -1) warehouses.value[idx] = updated
      toast.success(t('locations.warehouseUpdated'))
    } else {
      const created = await postWarehouse({ name: warehouseForm.value.name, location: warehouseFormLocationId.value })
      warehouses.value.push(created)
      toast.success(t('locations.warehouseAdded'))
    }
    closeWarehouseForm()
  } catch {
    toast.error(t('locations.warehouseSaveFailed'))
  } finally {
    warehouseSaving.value = false
  }
}

async function removeWarehouse(wh) {
  if (!confirm(t('locations.deleteWarehouseConfirm', { name: wh.name }))) return
  try {
    await deleteWarehouse(wh.id)
    warehouses.value = warehouses.value.filter(w => w.id !== wh.id)
    toast.success(t('locations.warehouseDeleted'))
  } catch {
    toast.error(t('locations.warehouseDeleteFailed'))
  }
}

onMounted(load)
</script>

